import React from 'react';
import { ArrowRight, Check } from 'lucide-react';
import BackgroundAnimation from '../../components/BackgroundAnimation';

const WebsiteDesign = () => {
  const features = [
    { title: 'Responsive Design', desc: 'Perfect display across all devices - desktop, tablet, and mobile' },
    { title: 'Lightning Fast', desc: 'Optimized for speed with Core Web Vitals scores under 2.5s' },
    { title: 'Secure & Reliable', desc: 'SSL certificates, regular backups, and security monitoring' },
    { title: 'SEO Optimized', desc: 'Built-in SEO features to rank higher in search results' },
    { title: 'User Experience', desc: 'Intuitive navigation and user-friendly interface design' },
    { title: 'Clean Code', desc: 'Well-structured, maintainable code for future updates' }
  ];

  const process = [
    { num: '01', title: 'Discovery & Strategy', desc: 'We analyze your business goals, target audience, and competitors to create a comprehensive strategy.' },
    { num: '02', title: 'Design & Prototyping', desc: 'Our designers create wireframes and high-fidelity mockups that align with your brand identity.' },
    { num: '03', title: 'Development & Testing', desc: 'We build your website using cutting-edge technologies and thoroughly test across all devices.' },
    { num: '04', title: 'Launch & Optimization', desc: 'We launch your website and continuously monitor performance for optimal results.' }
  ];

  const technologies = ['React & Next.js', 'TypeScript', 'Tailwind CSS', 'Framer Motion', 'Three.js', 'Spline 3D', 'Vercel', 'Netlify'];

  return (
    <div className="min-h-screen page-transition" style={{ background: 'var(--bg-primary)', paddingTop: '80px', position: 'relative' }}>
      <BackgroundAnimation />
      
      {/* Hero */}
      <section className="py-24" style={{ padding: '120px 7.6923% 80px' }}>
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="display-huge mb-6" style={{ color: 'var(--text-primary)' }}>
            Website Design &
            <span className="block" style={{ color: 'var(--brand-primary)' }}>
              Development
            </span>
          </h1>
          <p className="body-large mb-8" style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto 32px' }}>
            High-Performance Websites That Convert
          </p>
          <p className="body-medium mb-8" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto 32px' }}>
            We create stunning, fast-loading websites that not only look amazing but drive real business results. From concept to launch, we handle every aspect of your digital presence.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://calendly.com/americanprimeforyou"
              target="_blank"
              rel="noopener noreferrer"
              className="button-text dark-button-animate inline-flex items-center space-x-2"
              style={{ background: 'var(--brand-primary)', color: '#000000', padding: '14px 32px', borderRadius: '0px', textDecoration: 'none' }}
            >
              <span>Get Your Website Quote</span>
              <ArrowRight size={20} />
            </a>
            <a href="/services" className="button-text dark-button-animate" style={{ background: 'rgba(255, 255, 255, 0.1)', color: 'var(--text-primary)', padding: '14px 32px', borderRadius: '0px', textDecoration: 'none' }}>
              View All Services
            </a>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24" style={{ padding: '100px 7.6923%' }}>
        <div className="max-w-7xl mx-auto">
          <h2 className="display-large text-center mb-16" style={{ color: 'var(--text-primary)' }}>
            Why Choose Our
            <span style={{ color: 'var(--brand-primary)' }}> Web Development?</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, idx) => (
              <div key={idx} className="p-6 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid var(--border-subtle)' }}>
                <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{ background: 'rgba(0, 255, 209, 0.1)' }}>
                  <Check size={24} style={{ color: 'var(--brand-primary)' }} />
                </div>
                <h3 className="heading-2 mb-3" style={{ color: 'var(--text-primary)' }}>{feature.title}</h3>
                <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24" style={{ padding: '100px 7.6923%', background: 'rgba(0, 255, 209, 0.02)', borderTop: '1px solid rgba(0, 255, 209, 0.1)', borderBottom: '1px solid rgba(0, 255, 209, 0.1)' }}>
        <div className="max-w-7xl mx-auto">
          <h2 className="display-large text-center mb-16" style={{ color: 'var(--text-primary)' }}>
            Our Development Process
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, idx) => (
              <div key={idx}>
                <div className="display-large mb-4" style={{ color: 'var(--brand-primary)' }}>{step.num}</div>
                <h3 className="heading-2 mb-3" style={{ color: 'var(--text-primary)' }}>{step.title}</h3>
                <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-24" style={{ padding: '100px 7.6923%' }}>
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="display-large mb-8" style={{ color: 'var(--text-primary)' }}>Technologies We Use</h2>
          <p className="body-large mb-12" style={{ color: 'var(--text-secondary)' }}>We leverage the latest technologies to build modern, scalable, and maintainable websites</p>
          <div className="flex flex-wrap justify-center gap-4">
            {technologies.map((tech, idx) => (
              <div key={idx} className="px-6 py-3 rounded-lg" style={{ background: 'rgba(0, 255, 209, 0.1)', border: '1px solid var(--brand-primary)' }}>
                <span className="body-medium" style={{ color: 'var(--brand-primary)' }}>{tech}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24" style={{ padding: '100px 7.6923%' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="display-large mb-6" style={{ color: 'var(--text-primary)' }}>
            Ready to Build Your Dream Website?
          </h2>
          <p className="body-large mb-8" style={{ color: 'var(--text-secondary)' }}>Let's discuss your project and create something amazing together</p>
          <a href="https://calendly.com/americanprimeforyou" target="_blank" rel="noopener noreferrer" className="button-text dark-button-animate inline-flex items-center space-x-2" style={{ background: 'var(--brand-primary)', color: '#000000', padding: '14px 32px', borderRadius: '0px', textDecoration: 'none' }}>
            <span>Start Your Project</span>
            <ArrowRight size={20} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default WebsiteDesign;